import { Link } from "react-router-dom";
import { ArrowRight, Crosshair, ShieldAlert, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden pt-32 pb-20 lg:pt-40 bg-white">
      {/* Tactical grid background */}
      <div className="absolute inset-0 tactical-grid opacity-100" />

      {/* Diagonal red accent strip */}
      <div className="absolute top-0 right-0 w-1 h-full bg-red-600" />
      <div className="absolute top-0 right-2 w-px h-full bg-blue-600" />

      {/* Big watermark */}
      <div className="pointer-events-none select-none absolute -top-10 -left-10 hidden md:block">
        <span className="font-display font-black uppercase text-[14rem] leading-none text-slate-100 tracking-tighter whitespace-nowrap">MKC</span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 mb-6 fade-up">
            <span className="w-8 h-px bg-red-600" />
            <span className="overline text-red-600">// MKC · ENSURING SECURE ENVIRONMENTS</span>
          </div>

          <h1 className="font-display font-black uppercase text-[#0A1128] text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.92] fade-up">
            Precision-built<br />
            <span className="relative inline-block">
              <span className="text-red-600">security equipment</span>
              <span className="absolute -bottom-2 left-0 w-full h-1 bg-red-600/20" />
            </span><br />
            <span className="text-blue-600">for the front line.</span>
          </h1>

          <p className="mt-8 max-w-2xl text-base md:text-lg text-slate-700 leading-relaxed fade-up" style={{ animationDelay: "120ms" }}>
            {SITE.aboutShort} Bomb-disposal kits, metal detectors, manipulators, electronic stethoscopes,
            search lights and more — engineered for use in <span className="text-[#0A1128] font-semibold">critical, high-risk environments</span>.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up" style={{ animationDelay: "240ms" }}>
            <Button asChild data-testid="hero-cta-equipment" className="bg-red-600 hover:bg-red-700 text-white rounded-none uppercase font-bold tracking-wide h-14 px-8 text-sm">
              <Link to="/products">View Equipment <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button
              asChild
              data-testid="hero-cta-quote"
              variant="outline"
              className="border-2 border-blue-600 bg-white text-blue-600 hover:bg-blue-50 hover:text-blue-700 rounded-none uppercase font-bold tracking-wide h-14 px-8 text-sm"
            >
              <a href="#contact">Request Quote</a>
            </Button>
          </div>

          {/* feature pills */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-px border border-slate-200 bg-slate-200 max-w-3xl shadow-hard">
            {[
              { icon: Search, label: "Detection", value: "14 Models" },
              { icon: ShieldAlert, label: "EOD", value: "12 Systems" },
              { icon: Crosshair, label: "Surveillance", value: "8 Tools" },
            ].map((item) => (
              <div key={item.label} className="bg-white p-5 flex items-center gap-4">
                <div className="w-10 h-10 border border-slate-300 flex items-center justify-center bg-white">
                  <item.icon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <div className="overline">{item.label}</div>
                  <div className="font-display font-bold text-[#0A1128] text-sm mt-1">{item.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* HUD panel */}
        <div className="hidden lg:block lg:col-span-4">
          <div className="relative border border-slate-200 bg-white p-6 corner-tl corner-tr corner-bl corner-br shadow-hard">
            <div className="overline mb-4 text-red-600">// LIVE OPS PANEL</div>
            <div className="space-y-4">
              {[
                { k: "DEPLOYMENT", v: "PAN-INDIA", c: "text-emerald-600" },
                { k: "AGENCIES SERVED", v: "150+", c: "text-[#0A1128]" },
                { k: "PRODUCT LINES", v: "30+", c: "text-[#0A1128]" },
                { k: "RESPONSE", v: "< 24 HR", c: "text-blue-600" },
                { k: "STATUS", v: "ACCEPTING ORDERS", c: "text-red-600" },
              ].map((row) => (
                <div key={row.k} className="flex items-center justify-between border-b border-slate-200 pb-3 last:border-b-0 last:pb-0">
                  <span className="overline">{row.k}</span>
                  <span className={`font-display font-bold text-sm ${row.c}`}>{row.v}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 beam h-px bg-slate-200" />
            <p className="mt-4 text-xs font-mono-tactical text-slate-500 leading-relaxed">
              Manufacturer & supplier of certified security equipment for defense and law enforcement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
